import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { ApiError } from '@/lib/api';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export interface ResubmitPayload {
  /** Original job ID being revised */
  jobId: string;
  /** New contract file (upload new) or null (reuse original) */
  file: File | null;
  /** Optional gate config override JSON */
  gateConfigOverride?: Record<string, unknown>;
}

export interface ResubmitState {
  isSubmitting: boolean;
  uploadProgress: number | null;
  error: string | null;
}

export interface UseResubmitReturn extends ResubmitState {
  submit: (payload: ResubmitPayload) => Promise<void>;
  reset: () => void;
}

/**
 * FR-078: Mutation hook for POST /api/v1/jobs/{job_id}/resubmit.
 *
 * Uses XHR (not fetch) so upload progress is trackable.
 * On success navigates to the new job detail page.
 */
export function useResubmit(): UseResubmitReturn {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setIsSubmitting(false);
    setUploadProgress(null);
    setError(null);
  }

  async function submit(payload: ResubmitPayload): Promise<void> {
    setIsSubmitting(true);
    setUploadProgress(0);
    setError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      const formData = new FormData();
      if (payload.file) {
        formData.append('file', payload.file);
      }
      if (payload.gateConfigOverride) {
        formData.append('gate_config_override', JSON.stringify(payload.gateConfigOverride));
      }

      const newJobId = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_BASE}/api/v1/jobs/${payload.jobId}/resubmit`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 201) {
            const data = JSON.parse(xhr.responseText) as { job_id: string };
            resolve(data.job_id);
          } else {
            const msg =
              xhr.status === 400
                ? 'The uploaded file could not be processed. Please verify it is not corrupted.'
                : xhr.status === 403
                  ? 'You do not have permission to resubmit this contract.'
                  : `Resubmit failed (${xhr.status}). Please try again.`;
            reject(new ApiError(xhr.status, msg));
          }
        };

        xhr.onerror = () => {
          reject(new ApiError(0, 'Upload failed. Please check your connection and try again.'));
        };

        xhr.send(formData);
      });

      toast.success('Revision submitted successfully. Processing will begin shortly.');
      navigate(`/jobs/${newJobId}`);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : 'Resubmit failed. Please try again.';
      setError(message);
      toast.error(message);
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  }

  return { isSubmitting, uploadProgress, error, submit, reset };
}
